import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Heart, Search, Eye, Trash2, Download, CheckCircle, XCircle, Clock, Upload, Mail, Loader2, X, Calendar, Plus, ShieldCheck, Pencil, Save, Wand2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { formatDisplayDate } from '../../lib/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ScholarshipDonors = () => {
  const { user } = useAuth();
  const [donors, setDonors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [selectedDonor, setSelectedDonor] = useState(null);

  // Excel import state
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importYear, setImportYear] = useState(2025);

  // Reminder state
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderYear, setReminderYear] = useState(2026);
  const [reminderPreview, setReminderPreview] = useState(null);
  const [reminderCustomMsg, setReminderCustomMsg] = useState('');
  const [sendingReminders, setSendingReminders] = useState(false);
  const [reminderResult, setReminderResult] = useState(null);

  // Edit mode state for the donor detail modal
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [reclassifying, setReclassifying] = useState(false);

  // Yearly reporting filter (list page)
  const [reportYear, setReportYear] = useState('');   // '' = all years
  const [reportPaid, setReportPaid] = useState('all'); // 'all' | 'paid' | 'unpaid'

  // Record-Payment dialog state (donor detail modal)
  const [paymentDialog, setPaymentDialog] = useState(null); // { year, amount, paymentDate, paymentMethod, transactionId, notes }
  const [savingPayment, setSavingPayment] = useState(false);

  // Helpers over installments
  const paidYearsSet = (donor) =>
    new Set(((donor?.installments) || [])
      .filter(i => i.status === 'paid' || i.status === 'pending_verification')
      .map(i => i.year));
  const lastPaymentEntry = (donor) => {
    const paid = ((donor?.installments) || []).filter(i => i.status === 'paid' && i.paymentDate);
    if (!paid.length) return null;
    // Compare on paymentDate string (YYYY-MM-DD sorts naturally); fall back on recordedAt.
    return paid.slice().sort((a, b) => (b.paymentDate || '').localeCompare(a.paymentDate || ''))[0];
  };
  const totalPaidAmount = (donor) =>
    ((donor?.installments) || [])
      .filter(i => i.status === 'paid')
      .reduce((sum, i) => sum + (parseInt(i.amount) || 0), 0);

  useEffect(() => {
    fetchDonors();
  }, []);

  const fetchDonors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/scholarship/donors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDonors(response.data.donors || []);
    } catch (error) {
      toast.error('Failed to fetch donors');
    } finally {
      setLoading(false);
    }
  };

  const updateDonorStatus = async (donorId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/scholarship/donors/${donorId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Donor ${status} successfully`);
      fetchDonors();
      setSelectedDonor(null);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update status');
    }
  };

  const deleteDonor = async (id) => {
    if (!window.confirm('Are you sure you want to delete this donor registration?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/scholarship/donors/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Donor registration deleted successfully');
      fetchDonors();
    } catch (error) {
      toast.error('Failed to delete donor');
    }
  };

  const exportToCSV = () => {
    // Respect the active year + paid-status filter so admin can download exactly
    // what they see on screen (e.g. "who paid for 2026", "who hasn't paid for 2025").
    const rows = filteredDonors;
    const yr = reportYear ? parseInt(reportYear) : null;

    const baseHeaders = ['Name', 'Father/Husband Name', 'Nukh', 'Email', 'Phone', 'WhatsApp', 'City', 'State', 'Type', 'Pledged Amount', 'Status', 'Registration Date', 'Total Paid (₹)', 'Years Paid', 'Last Payment Date', 'Last Payment Amount (₹)', 'Last Payment Method', 'Last Payment Notes'];
    const yearHeaders = yr ? [`${yr} Status`, `${yr} Paid On`, `${yr} Amount (₹)`, `${yr} Method`, `${yr} Notes`] : [];
    const headers = [...baseHeaders, ...yearHeaders];

    const csvData = rows.map(d => {
      const paidYears = [...paidYearsSet(d)].sort();
      const last = lastPaymentEntry(d);
      const total = totalPaidAmount(d);
      const yrEntry = yr ? ((d.installments || []).find(i => i.year === yr) || null) : null;
      const base = [
        d.fullName || '',
        d.fatherOrHusbandName || '',
        d.nukh || '',
        d.email || '',
        d.phone || '',
        d.whatsapp || '',
        d.city || '',
        d.state || '',
        d.membershipType === 'shiksha_parammitra' ? 'Shiksha Parammitra' : 'Shiksha Mitra',
        d.amount ? `${d.amount}` : '',
        d.status || '',
        formatDisplayDate(d.registrationDate),
        `${total}`,
        paidYears.join(', '),
        last?.paymentDate || '',
        last?.amount ? `${last.amount}` : '',
        last?.paymentMethod || '',
        last?.notes || '',
      ];
      if (!yr) return base;
      return [
        ...base,
        yrEntry?.status || 'unpaid',
        yrEntry?.paymentDate || '',
        yrEntry?.amount ? `${yrEntry.amount}` : '',
        yrEntry?.paymentMethod || '',
        yrEntry?.notes || '',
      ];
    });

    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csvContent = [headers, ...csvData].map(row => row.map(escape).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const suffix = yr ? `_${yr}_${reportPaid}` : '';
    a.download = `scholarship_donors${suffix}.csv`;
    a.click();
  };

  // ---- Excel import ----
  const handleImportClick = () => fileInputRef.current?.click();

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('installmentYear', String(importYear));
      fd.append('installmentStatus', 'paid');
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API}/scholarship/donors/import`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setImportResult(data);
      toast.success(`Imported ${data.imported} donors${data.skipped ? `, skipped ${data.skipped}` : ''}`);
      fetchDonors();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Import failed';
      toast.error(typeof msg === 'string' ? msg : 'Import failed — check console');
      setImportResult({ success: false, error: msg });
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ---- Reminders ----
  const openReminder = async () => {
    setReminderOpen(true);
    setReminderResult(null);
    await loadReminderPreview(reminderYear);
  };

  const loadReminderPreview = async (year) => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/scholarship/donors/reminder-preview`, {
        params: { reminderYear: year },
        headers: { Authorization: `Bearer ${token}` },
      });
      setReminderPreview(data);
    } catch {
      toast.error('Failed to load reminder preview');
    }
  };

  const sendReminders = async () => {
    if (!reminderPreview || reminderPreview.eligibleCount === 0) {
      toast.error('No eligible donors to remind');
      return;
    }
    if (!window.confirm(`Send ${reminderYear} installment reminders to ${reminderPreview.eligibleCount} donors?`)) return;
    setSendingReminders(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API}/scholarship/donors/send-reminders`,
        { reminderYear, customMessage: reminderCustomMsg || null, payBaseUrl: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReminderResult(data);
      toast.success(`Queued ${data.queued} reminder emails`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  // ---- Installment management (admin) ----
  const refreshSelectedDonor = async (donorId) => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/scholarship/donors`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 100 },
      });
      const fresh = (data.donors || []).find(d => d.id === donorId);
      if (fresh) setSelectedDonor(fresh);
      setDonors(data.donors || []);
    } catch {
      /* ignore */
    }
  };

  const openPaymentDialog = (year, prefillAmount = 0) => {
    const existing = (selectedDonor?.installments || []).find(i => i.year === year) || {};
    setPaymentDialog({
      year,
      amount: existing.amount || prefillAmount || selectedDonor?.amount || 0,
      paymentDate: existing.paymentDate || new Date().toISOString().slice(0, 10),
      paymentMethod: existing.paymentMethod || '',
      transactionId: existing.transactionId || '',
      notes: existing.notes || '',
    });
  };

  const savePaymentDialog = async () => {
    if (!selectedDonor || !paymentDialog) return;
    const token = localStorage.getItem('token');
    setSavingPayment(true);
    try {
      await axios.post(
        `${API}/scholarship/donors/${selectedDonor.id}/installments`,
        { ...paymentDialog, status: 'paid' },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`${paymentDialog.year} payment recorded`);
      setPaymentDialog(null);
      await refreshSelectedDonor(selectedDonor.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSavingPayment(false);
    }
  };

  const markInstallmentPaid = async (donorId, year, amount) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/scholarship/donors/${donorId}/installments`,
        { year, status: 'paid', amount, paymentDate: new Date().toISOString().slice(0, 10) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${year} marked as paid`);
      await refreshSelectedDonor(donorId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const verifyInstallment = async (donorId, year) => {
    const token = localStorage.getItem('token');
    try {
      await axios.patch(`${API}/scholarship/donors/${donorId}/installments/${year}/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${year} verified`);
      await refreshSelectedDonor(donorId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to verify');
    }
  };

  const deleteInstallment = async (donorId, year) => {
    if (!window.confirm(`Remove the ${year} installment for this donor?`)) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/scholarship/donors/${donorId}/installments/${year}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${year} installment removed`);
      await refreshSelectedDonor(donorId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  // ---- Edit donor fields ----
  const startEdit = () => {
    if (!selectedDonor) return;
    setEditForm({
      fullName: selectedDonor.fullName || '',
      fatherOrHusbandName: selectedDonor.fatherOrHusbandName || '',
      nukh: selectedDonor.nukh || '',
      email: selectedDonor.email || '',
      phone: selectedDonor.phone || '',
      whatsapp: selectedDonor.whatsapp || '',
      address: selectedDonor.address || '',
      city: selectedDonor.city || '',
      state: selectedDonor.state || '',
      occupation: selectedDonor.occupation || '',
      membershipType: selectedDonor.membershipType === 'shiksha_parammitra' ? 'shiksha_parammitra' : 'shiksha_mitra',
      membershipNo: selectedDonor.membershipNo || '',
      amount: selectedDonor.amount || '',
      message: selectedDonor.message || '',
    });
    setEditMode(true);
  };

  const cancelEdit = () => { setEditMode(false); setEditForm({}); };

  const saveEdit = async () => {
    if (!selectedDonor) return;
    setSavingEdit(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/scholarship/donors/${selectedDonor.id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Donor updated');
      setEditMode(false);
      await refreshSelectedDonor(selectedDonor.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSavingEdit(false);
    }
  };

  const reclassifyAll = async () => {
    if (!window.confirm('Auto-classify all donors as Shiksha Mitra or Parammitra based on amount and label?\n\nThis is a safe operation — only the membershipType field is updated.')) return;
    setReclassifying(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API}/scholarship/donors/reclassify-membership`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Reclassified ${data.reclassified} donors`);
      fetchDonors();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reclassify');
    } finally {
      setReclassifying(false);
    }
  };

  const filteredDonors = donors.filter(donor => {
    const matchesSearch = donor.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         donor.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         donor.phone?.includes(searchTerm);
    const matchesType = filterType === 'all' || donor.membershipType === filterType;
    // Yearly report filter
    let matchesYear = true;
    if (reportYear) {
      const yr = parseInt(reportYear);
      const paidThisYear = paidYearsSet(donor).has(yr);
      if (reportPaid === 'paid') matchesYear = paidThisYear;
      else if (reportPaid === 'unpaid') matchesYear = !paidThisYear;
    }
    return matchesSearch && matchesType && matchesYear;
  });

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
    };
    const icons = {
      pending: <Clock className="w-3 h-3" />,
      approved: <CheckCircle className="w-3 h-3" />,
      rejected: <XCircle className="w-3 h-3" />,
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {icons[status]} {status}
      </span>
    );
  };

  const getMembershipBadge = (type) => {
    const isParammitra = type === 'shiksha_parammitra';
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${isParammitra ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
        {isParammitra ? 'Shiksha Parammitra' : 'Shiksha Mitra'}
      </span>
    );
  };

  const totalAmount = donors.reduce((sum, d) => sum + (d.amount || 0), 0);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif font-bold text-3xl text-primary mb-2">Scholarship Donors</h1>
          <p className="text-muted-foreground">Shiksha Mitra & Parammitra registrations</p>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="file"
            accept=".xlsx,.xls,.xlsm"
            ref={fileInputRef}
            onChange={handleImportFile}
            className="hidden"
            data-testid="donor-import-file-input"
          />
          <select
            value={importYear}
            onChange={(e) => setImportYear(parseInt(e.target.value, 10))}
            className="h-9 px-2 rounded-md border border-gray-200 text-sm bg-white"
            title="Year that imported rows represent (each row gets a paid installment for this year)"
            data-testid="donor-import-year"
          >
            {[2023, 2024, 2025, 2026].map(y => <option key={y} value={y}>{y} Import</option>)}
          </select>
          <Button variant="outline" onClick={handleImportClick} disabled={importing} data-testid="donor-import-btn">
            {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Import Excel
          </Button>
          <Button variant="outline" onClick={openReminder} className="border-orange-300 text-orange-700 hover:bg-orange-50" data-testid="donor-send-reminders-btn">
            <Mail className="w-4 h-4 mr-2" /> Send {reminderYear} Reminders
          </Button>
          <Button variant="outline" onClick={reclassifyAll} disabled={reclassifying} title="Auto-classify donors as Shiksha Mitra / Parammitra based on amount + label" data-testid="donor-reclassify-btn">
            {reclassifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />} Reclassify Types
          </Button>
          <Button variant="outline" onClick={exportToCSV} data-testid="donor-export-btn" title={reportYear ? `Download filtered donors with ${reportYear} payment columns` : 'Download donors CSV (installments summary)'}>
            <Download className="w-4 h-4 mr-2" /> Export CSV{reportYear ? ` · ${reportYear}` : ''}
          </Button>
          <div className="text-right">
            <div className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-red-500" />
              <span className="text-lg font-semibold">{donors.length} Donors</span>
            </div>
            <p className="text-sm text-muted-foreground">Total: ₹{totalAmount.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Import result banner */}
      {importResult && (
        <div className={`mb-4 p-4 rounded-md border ${importResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} flex items-start gap-3`} data-testid="donor-import-result">
          <div className="flex-1">
            {importResult.success ? (
              <>
                <p className="font-medium text-green-800">Excel import successful</p>
                <p className="text-sm text-green-700 mt-0.5">
                  Imported <b>{importResult.imported}</b> donor{importResult.imported === 1 ? '' : 's'}
                  {importResult.skipped ? <> · Skipped <b>{importResult.skipped}</b> row{importResult.skipped === 1 ? '' : 's'}</> : null}
                  {' · '}each row tagged as <b>{importResult.installmentStatus} {importResult.installmentYear}</b>
                </p>
                {importResult.detectedColumns && (
                  <details className="mt-2 text-xs text-green-700">
                    <summary className="cursor-pointer">View detected column mapping</summary>
                    <ul className="mt-1 ml-3 list-disc space-y-0.5">
                      {Object.entries(importResult.detectedColumns).map(([k, v]) => (
                        <li key={k}><span className="font-mono">{k}</span> ← <span className="font-mono">{v || '—'}</span></li>
                      ))}
                    </ul>
                  </details>
                )}
                {importResult.errors?.length > 0 && (
                  <details className="mt-2 text-xs text-amber-700">
                    <summary className="cursor-pointer">View skipped rows ({importResult.errors.length})</summary>
                    <ul className="mt-1 ml-3 list-disc">
                      {importResult.errors.map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
                    </ul>
                  </details>
                )}
              </>
            ) : (
              <>
                <p className="font-medium text-red-800">Import failed</p>
                <p className="text-sm text-red-700 mt-0.5">{importResult.error}</p>
              </>
            )}
          </div>
          <button onClick={() => setImportResult(null)} className="text-gray-400 hover:text-gray-600" aria-label="Dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Reminder dialog */}
      {reminderOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setReminderOpen(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="reminder-dialog">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Calendar className="w-5 h-5 text-orange-600" /> Send Installment Reminders</h3>
              <button onClick={() => setReminderOpen(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Reminder Year</label>
                <select
                  value={reminderYear}
                  onChange={(e) => { const y = parseInt(e.target.value, 10); setReminderYear(y); loadReminderPreview(y); }}
                  className="mt-1 w-full h-9 px-3 rounded-md border border-gray-200 text-sm bg-white"
                  data-testid="reminder-year-select"
                >
                  {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <p className="text-xs text-gray-500 mt-1">Donors who already have a <b>paid</b> entry for this year will be excluded.</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Custom message (optional)</label>
                <textarea
                  rows={3}
                  value={reminderCustomMsg}
                  onChange={(e) => setReminderCustomMsg(e.target.value)}
                  placeholder="Leave blank to use the default reminder message."
                  className="mt-1 w-full px-3 py-2 rounded-md border border-gray-200 text-sm bg-white"
                  data-testid="reminder-custom-msg"
                />
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-md p-3">
                {reminderPreview ? (
                  <>
                    <p className="text-sm font-medium text-orange-900">
                      {reminderPreview.eligibleCount} donor{reminderPreview.eligibleCount === 1 ? '' : 's'} will receive a {reminderYear} reminder
                    </p>
                    {reminderPreview.eligibleCount > 0 && (
                      <details className="mt-1 text-xs text-orange-800">
                        <summary className="cursor-pointer">Show recipient list (first 20)</summary>
                        <ul className="mt-1 max-h-40 overflow-y-auto">
                          {reminderPreview.donors.slice(0, 20).map(d => (
                            <li key={d.id} className="py-0.5">{d.fullName} — {d.email}</li>
                          ))}
                          {reminderPreview.donors.length > 20 && (
                            <li className="text-orange-600 italic">…and {reminderPreview.donors.length - 20} more</li>
                          )}
                        </ul>
                      </details>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-orange-700"><Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Loading preview…</p>
                )}
              </div>
              {reminderResult && (
                <div className="bg-green-50 border border-green-200 rounded-md p-3 text-sm text-green-800">
                  ✓ {reminderResult.message}. Emails are being delivered in the background.
                </div>
              )}
            </div>
            <div className="px-5 py-4 border-t bg-gray-50 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setReminderOpen(false)}>Close</Button>
              <Button
                onClick={sendReminders}
                disabled={sendingReminders || !reminderPreview || reminderPreview.eligibleCount === 0}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="reminder-send-btn"
              >
                {sendingReminders ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Mail className="w-4 h-4 mr-2" /> Send Reminders</>}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2">
            {[{ value: 'all', label: 'All' }, { value: 'shiksha_mitra', label: 'Shiksha Mitra' }, { value: 'shiksha_parammitra', label: 'Parammitra' }].map((type) => (
              <Button
                key={type.value}
                variant={filterType === type.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterType(type.value)}
              >
                {type.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Yearly report filter */}
        <div className="flex flex-col md:flex-row md:items-center gap-3 mt-3 pt-3 border-t border-dashed">
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" /> Year Report
          </label>
          <select
            value={reportYear}
            onChange={(e) => setReportYear(e.target.value)}
            className="h-9 px-2 rounded-md border border-gray-200 text-sm bg-white"
            data-testid="donor-report-year"
          >
            <option value="">All years</option>
            {[2023, 2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <div className="flex gap-1.5">
            {[{ v: 'all', l: 'All' }, { v: 'paid', l: 'Paid' }, { v: 'unpaid', l: 'Not Paid' }].map(o => (
              <Button
                key={o.v}
                size="sm"
                variant={reportPaid === o.v ? 'default' : 'outline'}
                onClick={() => setReportPaid(o.v)}
                disabled={!reportYear}
                data-testid={`donor-report-paid-${o.v}`}
              >
                {o.l}
              </Button>
            ))}
          </div>
          {reportYear && (
            <span className="text-xs text-gray-500 ml-auto" data-testid="donor-report-count">
              Showing <b className="text-gray-900">{filteredDonors.length}</b> of {donors.length} donors
            </span>
          )}
        </div>
      </Card>

      {/* Donors Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">Donor</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Contact</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Type</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Amount</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Last Payment</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                    Loading donors...
                  </td>
                </tr>
              ) : filteredDonors.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                    No donors found
                  </td>
                </tr>
              ) : (
                filteredDonors.map((donor) => (
                  <tr key={donor.id} className="hover:bg-muted/50">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-foreground">{donor.fullName}</p>
                      <p className="text-sm text-muted-foreground">{donor.fatherOrHusbandName}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm">{donor.email}</p>
                      <p className="text-sm text-muted-foreground">{donor.phone}</p>
                    </td>
                    <td className="px-4 py-3">
                      {getMembershipBadge(donor.membershipType)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-green-600">₹{donor.amount?.toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(donor.status)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {(() => {
                        const last = lastPaymentEntry(donor);
                        if (!last) return <span className="text-gray-400">—</span>;
                        return (
                          <div className="flex flex-col leading-tight">
                            <span className="font-medium text-gray-800">{formatDisplayDate(last.paymentDate)}</span>
                            <span className="text-xs text-gray-500">{last.year} · ₹{(last.amount || 0).toLocaleString()}</span>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {formatDisplayDate(donor.registrationDate)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setSelectedDonor(donor)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        {donor.status === 'pending' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-green-600 hover:text-green-700"
                            onClick={() => updateDonorStatus(donor.id, 'approved')}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        )}
                        {!(donor.status === 'approved' && !user?.superAdmin) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => deleteDonor(donor.id)}
                            data-testid={`delete-donor-${donor.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Detail Modal */}
      {selectedDonor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setSelectedDonor(null); setEditMode(false); }}>
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  {editMode ? (
                    <input
                      className="font-serif font-bold text-2xl text-primary border-b border-gray-300 focus:border-orange-500 outline-none bg-transparent w-full"
                      value={editForm.fullName}
                      onChange={(e) => setEditForm(f => ({ ...f, fullName: e.target.value }))}
                      data-testid="edit-fullName"
                    />
                  ) : (
                    <h2 className="font-serif font-bold text-2xl text-primary">{selectedDonor.fullName}</h2>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    {editMode ? (
                      <select
                        value={editForm.membershipType}
                        onChange={(e) => setEditForm(f => ({ ...f, membershipType: e.target.value }))}
                        className="text-xs px-2 py-1 rounded-md border border-gray-300"
                        data-testid="edit-membershipType"
                      >
                        <option value="shiksha_mitra">Shiksha Mitra</option>
                        <option value="shiksha_parammitra">Shiksha Parammitra</option>
                      </select>
                    ) : getMembershipBadge(selectedDonor.membershipType)}
                    {getStatusBadge(selectedDonor.status)}
                  </div>
                </div>
                <div className="text-right">
                  {editMode ? (
                    <div className="flex items-center gap-1">
                      <span className="text-2xl font-bold text-green-600">₹</span>
                      <input
                        type="number" min="0"
                        className="w-28 text-2xl font-bold text-green-600 border-b border-gray-300 focus:border-orange-500 outline-none bg-transparent text-right"
                        value={editForm.amount}
                        onChange={(e) => setEditForm(f => ({ ...f, amount: e.target.value }))}
                        data-testid="edit-amount"
                      />
                    </div>
                  ) : (
                    <p className="text-2xl font-bold text-green-600">₹{selectedDonor.amount?.toLocaleString()}</p>
                  )}
                  <div className="mt-2 flex justify-end gap-1.5">
                    {!editMode ? (
                      <Button size="sm" variant="outline" onClick={startEdit} data-testid="donor-edit-btn" className="text-orange-700 border-orange-300 hover:bg-orange-50">
                        <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                      </Button>
                    ) : (
                      <>
                        <Button size="sm" variant="outline" onClick={cancelEdit} disabled={savingEdit}>
                          <X className="w-3.5 h-3.5 mr-1" /> Cancel
                        </Button>
                        <Button size="sm" onClick={saveEdit} disabled={savingEdit} className="bg-orange-600 hover:bg-orange-700" data-testid="donor-edit-save">
                          {savingEdit ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                {[
                  { key: 'fatherOrHusbandName', label: 'Father/Husband Name', col: 1 },
                  { key: 'nukh', label: 'Nukh', col: 1 },
                  { key: 'email', label: 'Email', type: 'email', col: 1 },
                  { key: 'phone', label: 'Phone', col: 1 },
                  { key: 'whatsapp', label: 'WhatsApp', col: 1 },
                  { key: 'occupation', label: 'Occupation', col: 1 },
                  { key: 'address', label: 'Address', col: 2 },
                  { key: 'city', label: 'City', col: 1 },
                  { key: 'state', label: 'State', col: 1 },
                  { key: 'membershipNo', label: 'Membership No.', col: 1 },
                  { key: 'message', label: 'Message / Notes', col: 2 },
                ].map(({ key, label, type, col }) => (
                  <div key={key} className={col === 2 ? 'col-span-2' : ''}>
                    <p className="text-sm text-muted-foreground">{label}</p>
                    {editMode ? (
                      <Input
                        type={type || 'text'}
                        value={editForm[key] || ''}
                        onChange={(e) => setEditForm(f => ({ ...f, [key]: e.target.value }))}
                        className="mt-0.5 h-8"
                        data-testid={`edit-${key}`}
                      />
                    ) : (
                      <p className="font-medium">{selectedDonor[key] || '-'}</p>
                    )}
                  </div>
                ))}
                <div>
                  <p className="text-sm text-muted-foreground">Payment Method</p>
                  <p className="font-medium">{selectedDonor.paymentMethod || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Transaction ID</p>
                  <p className="font-medium font-mono">{selectedDonor.transactionId || '-'}</p>
                </div>
              </div>

              {/* Yearly installments timeline */}
              <div className="mb-6 border-t pt-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-orange-600" /> Yearly Installments
                  </h3>
                  <div className="flex flex-col items-end">
                    <span className="text-xs text-gray-500">{(selectedDonor.installments || []).length} year{(selectedDonor.installments || []).length === 1 ? '' : 's'} on record</span>
                    {(() => {
                      const last = lastPaymentEntry(selectedDonor);
                      return last ? (
                        <span className="text-xs text-gray-700 mt-0.5" data-testid="donor-last-payment">
                          Last payment: <b>{formatDisplayDate(last.paymentDate)}</b> · ₹{(last.amount || 0).toLocaleString()} ({last.year})
                        </span>
                      ) : null;
                    })()}
                  </div>
                </div>
                {(selectedDonor.installments || []).length === 0 && (
                  <p className="text-sm text-gray-400 italic">No installments recorded yet.</p>
                )}
                <div className="space-y-1.5" data-testid="installment-timeline">
                  {[...(selectedDonor.installments || [])]
                    .sort((a, b) => (a.year || 0) - (b.year || 0))
                    .map((inst) => {
                      const isPaid = inst.status === 'paid';
                      const isPending = inst.status === 'pending_verification';
                      return (
                        <div
                          key={inst.year}
                          className={`px-3 py-2 rounded-md text-sm ${
                            isPaid ? 'bg-green-50 border border-green-100' :
                            isPending ? 'bg-amber-50 border border-amber-200' :
                            'bg-gray-50 border border-gray-200'
                          }`}
                          data-testid={`installment-row-${inst.year}`}
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono font-semibold text-gray-800 w-12">{inst.year}</span>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase font-bold ${
                              isPaid ? 'bg-green-100 text-green-800' :
                              isPending ? 'bg-amber-100 text-amber-800' :
                              'bg-gray-200 text-gray-700'
                            }`}>{inst.status}</span>
                            <span className="text-gray-700 font-medium">₹{(inst.amount || 0).toLocaleString()}</span>
                            {inst.paymentDate && <span className="text-xs text-gray-500">on {formatDisplayDate(inst.paymentDate)}</span>}
                            {inst.paymentMethod && <span className="text-xs text-gray-500">· {inst.paymentMethod}</span>}
                            {inst.transactionId && <span className="text-xs text-gray-500 font-mono">· {inst.transactionId}</span>}
                            {inst.receiptUrl && (
                              <a href={inst.receiptUrl} target="_blank" rel="noreferrer" className="text-xs text-orange-600 hover:underline">receipt</a>
                            )}
                            <div className="ml-auto flex gap-1">
                              <button
                                onClick={() => openPaymentDialog(inst.year, inst.amount)}
                                className="text-blue-700 hover:bg-blue-100 rounded p-1"
                                title="Edit payment details"
                                data-testid={`edit-installment-${inst.year}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              {isPending && (
                                <button
                                  onClick={() => verifyInstallment(selectedDonor.id, inst.year)}
                                  className="text-green-700 hover:bg-green-100 rounded p-1"
                                  title="Verify & mark as paid"
                                  data-testid={`verify-installment-${inst.year}`}
                                >
                                  <ShieldCheck className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={() => deleteInstallment(selectedDonor.id, inst.year)}
                                className="text-red-600 hover:bg-red-50 rounded p-1"
                                title="Remove this installment"
                                data-testid={`delete-installment-${inst.year}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          {inst.notes && (
                            <p className="text-xs text-gray-600 mt-1 italic pl-14">Note: {inst.notes}</p>
                          )}
                        </div>
                      );
                    })}
                </div>

                {/* Add a new year's payment */}
                <div className="mt-3 pt-3 border-t border-dashed">
                  <p className="text-xs text-gray-500 mb-1.5">Record payment for a year (opens dialog with date, method, notes):</p>
                  <div className="flex flex-wrap gap-1.5">
                    {[2024, 2025, 2026, 2027, 2028, 2029, 2030, 2031].map(y => {
                      const existing = (selectedDonor.installments || []).find(i => i.year === y);
                      if (existing) return null;
                      return (
                        <button
                          key={y}
                          onClick={() => openPaymentDialog(y, selectedDonor.amount || 0)}
                          className="text-xs px-2 py-1 rounded-md border border-orange-200 text-orange-700 hover:bg-orange-50 inline-flex items-center gap-1"
                          data-testid={`add-installment-${y}`}
                        >
                          <Plus className="w-3 h-3" /> {y}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                {selectedDonor.status === 'pending' && (
                  <>
                    <Button
                      variant="outline"
                      className="text-red-600"
                      onClick={() => updateDonorStatus(selectedDonor.id, 'rejected')}
                    >
                      <XCircle className="w-4 h-4 mr-2" /> Reject
                    </Button>
                    <Button
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => updateDonorStatus(selectedDonor.id, 'approved')}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" /> Approve
                    </Button>
                  </>
                )}
                <Button variant="outline" onClick={() => setSelectedDonor(null)}>
                  Close
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Record Payment Dialog */}
      {paymentDialog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={() => !savingPayment && setPaymentDialog(null)}>
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="payment-dialog">
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <h3 className="text-base font-semibold flex items-center gap-2">
                <Calendar className="w-4 h-4 text-orange-600" /> Record Payment · {paymentDialog.year}
              </h3>
              {!savingPayment && (
                <button onClick={() => setPaymentDialog(null)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
              )}
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">Payment Date</label>
                  <Input
                    type="date"
                    value={paymentDialog.paymentDate}
                    onChange={(e) => setPaymentDialog(p => ({ ...p, paymentDate: e.target.value }))}
                    data-testid="payment-date"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">Actual date the payment was received (not today).</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">Amount (₹)</label>
                  <Input
                    type="number"
                    value={paymentDialog.amount}
                    onChange={(e) => setPaymentDialog(p => ({ ...p, amount: parseInt(e.target.value) || 0 }))}
                    data-testid="payment-amount"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">Method</label>
                  <select
                    value={paymentDialog.paymentMethod}
                    onChange={(e) => setPaymentDialog(p => ({ ...p, paymentMethod: e.target.value }))}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    data-testid="payment-method"
                  >
                    <option value="">Select method…</option>
                    <option value="cash">Cash</option>
                    <option value="cheque">Cheque</option>
                    <option value="upi">UPI</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="online">Online</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">Reference / Txn ID</label>
                  <Input
                    value={paymentDialog.transactionId}
                    onChange={(e) => setPaymentDialog(p => ({ ...p, transactionId: e.target.value }))}
                    placeholder="e.g. UPI ref / cheque no."
                    data-testid="payment-txn"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">Notes / How received</label>
                <textarea
                  value={paymentDialog.notes}
                  onChange={(e) => setPaymentDialog(p => ({ ...p, notes: e.target.value }))}
                  placeholder="e.g. Cash received in person at event, cheque #123 dated 12-Jul, etc."
                  className="w-full min-h-[70px] px-3 py-2 rounded-md border border-input bg-background text-sm"
                  data-testid="payment-notes"
                />
              </div>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPaymentDialog(null)} disabled={savingPayment}>Cancel</Button>
              <Button onClick={savePaymentDialog} disabled={savingPayment} className="bg-green-600 hover:bg-green-700" data-testid="payment-save">
                {savingPayment ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Save Payment
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScholarshipDonors;
